import { Card, Input, InputNumber, Select, Tabs } from 'antd';
import { LanguageInterface } from 'content/global-settings/types/globalSettingType';
import React from 'react';
import { I18nFieldData } from '../../../../shared/types/i18n/i18nTypes';
// [BEE-5023] Live channel picker (ODX admin channel fetch) 임시 비활성화 — 아래 Watch CTA
// live service/channel 블록과 함께 주석 처리.
// import LiveChannelPicker from './LiveChannelPicker';
import {
  CTA_TYPE_OPTIONS,
  CtaType,
  // LIVE_SERVICE_OPTIONS, // live service/channel 블록 비활성화로 미사용
  // LiveService,
  ORIENTATION_OPTIONS,
  ShortsMetaInterface,
  SUBTITLE_BURNED_LANG_OPTIONS,
  SUBTYPE_OPTIONS,
} from '../types/shortsMetaType';

interface Props {
  value: Partial<ShortsMetaInterface>;
  onChange: (next: Partial<ShortsMetaInterface>) => void;
  /** 사용할 언어 목록. 부모(EpisodeEdit)가 이미 fetch 한 writeLanguages 전달. */
  writeLanguages?: LanguageInterface[];
}

/** writeLanguages 미주어지면 fallback (영문/한국어만). */
const FALLBACK_LANGS: { rfc_code: string }[] = [
  { rfc_code: 'en' },
  { rfc_code: 'ko' },
];

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 500,
  marginBottom: 4,
};

/**
 * EpisodeEdit 의 content_type='shorts' 일 때 노출되는 ShortsMeta 입력 섹션.
 * 별도 form 이 아닌 controlled 컴포넌트 (props value/onChange).
 *
 * BEE-5022 (PRD v1.4):
 * - subtype 은 select 로 표시 (DB 는 free string 유지)
 * - internal_title / shorts_description / cta_* / start_timestamp 신규
 * - parent_program_logo 는 read-only preview (실제 업로드는 series/season)
 */
const ShortsMetaSection: React.FC<Props> = ({ value, onChange, writeLanguages }) => {
  const set = (patch: Partial<ShortsMetaInterface>) => onChange({ ...value, ...patch });

  const ctaType = (value.cta_type as CtaType) ?? 'internal';
  const isVod = value.subtype === 'vod' || value.subtype == null || value.subtype === '';
  // [BEE-5023] live service/channel 블록 비활성화로 미사용
  // const isLive = value.subtype === 'live';

  const langs = writeLanguages?.length ? writeLanguages : FALLBACK_LANGS;
  const i18nDesc: I18nFieldData = (value.i18n_shorts_description as I18nFieldData) ?? {};
  const setI18nDesc = (next: I18nFieldData) => {
    const koValue = next?.ko || Object.values(next || {}).find(v => v) || '';
    set({
      i18n_shorts_description: next,
      shorts_description: koValue || null,
    });
  };

  return (
    <Card title="Shorts Meta" style={{ marginBottom: 16 }}>
      <div style={{ display: 'grid', gap: 12 }}>
        {value.parent_program_logo ? (
          <div>
            <label style={labelStyle}>Parent program logo (read-only)</label>
            <img
              src={value.parent_program_logo}
              alt="parent program logo"
              style={{
                maxHeight: 80,
                background: '#222',
                padding: 8,
                borderRadius: 4,
              }}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Season → Series 에서 자동으로 채워짐. 변경하려면 해당 Season 또는
              Series 편집 페이지로.
            </div>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Subtype</label>
            <Select
              allowClear
              placeholder="Select subtype"
              style={{ width: '100%' }}
              value={(value.subtype as string) || undefined}
              onChange={v => set({ subtype: v || null })}
              options={SUBTYPE_OPTIONS}
            />
          </div>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Internal title (admin-only)</label>
            <Input
              placeholder="운영자가 보는 라벨"
              value={value.internal_title ?? ''}
              onChange={e => set({ internal_title: e.target.value })}
            />
          </div>
        </div>

        <div>
          {/* BEE-5028: Description i18n — EpisodeEdit 의 i18n_title 과 동일한 Tabs 패턴.
              i18n_shorts_description (JSON dict) 우선, shorts_description 은 default
              language (ko 또는 첫 non-empty) 로 자동 sync. */}
          <label style={labelStyle}>Description (사용자 노출)</label>
          <Tabs
            type="card"
            items={langs.map(lang => {
              const code = lang.rfc_code.toLowerCase();
              return {
                key: code,
                label: `Description ${lang.rfc_code}`,
                children: (
                  <Input.TextArea
                    rows={3}
                    placeholder="빈 값이면 episode/season/series synopsis 로 fallback"
                    value={(i18nDesc as any)[code] || ''}
                    onChange={e =>
                      setI18nDesc({ ...i18nDesc, [code]: e.target.value } as I18nFieldData)
                    }
                  />
                ),
              };
            })}
          />
        </div>

        {/*
          PRD v1.4 / BEE-5023: 4 flag (commerce/live/ai_generated/human_reviewed)
          UI 숨김. DB 컬럼은 유지 — commerce 는 별도 ShortsCommerce 모델, live 는
          subtype='live' 가 대체, ai_generated/human_reviewed 는 BEBRIDGE 자동 트랙
          OOS (PRD #33) 로 검수 흐름 자체 사라짐.
        */}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Orientation</label>
            <Select
              allowClear
              placeholder="Select orientation"
              style={{ width: '100%' }}
              value={value.orientation ?? undefined}
              onChange={v => set({ orientation: v ?? null })}
              options={ORIENTATION_OPTIONS}
            />
          </div>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Subtitle burned language</label>
            <Select
              allowClear
              placeholder="Select subtitle language"
              style={{ width: '100%' }}
              value={value.subtitle_burned_lang ?? undefined}
              onChange={v => set({ subtitle_burned_lang: v ?? null })}
              options={SUBTITLE_BURNED_LANG_OPTIONS}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Start offset (ms)</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={value.start_offset_ms ?? null}
              onChange={v => set({ start_offset_ms: v === null ? null : Number(v) })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>End offset (ms)</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={value.end_offset_ms ?? null}
              onChange={v => set({ end_offset_ms: v === null ? null : Number(v) })}
            />
          </div>
        </div>

        <Card type="inner" title="Watch CTA" size="small">
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={labelStyle}>CTA type</label>
              <Select
                style={{ width: 240 }}
                value={ctaType}
                onChange={v => set({ cta_type: v })}
                options={CTA_TYPE_OPTIONS}
              />
            </div>

            {ctaType === 'internal' && isVod ? (
              <div>
                <label style={labelStyle}>Start timestamp (seconds, VOD deeplink)</label>
                <InputNumber
                  min={0}
                  style={{ width: 240 }}
                  value={value.start_timestamp ?? null}
                  onChange={v =>
                    set({ start_timestamp: v === null ? null : Number(v) })
                  }
                />
              </div>
            ) : null}

            {/*
              [BEE-5023] Watch CTA 의 live Service + Channel 선택 영역 임시 비활성화.
              채널 정보를 ODX admin(mediaio)에서 fetch 하는 부분 전체를 페이지에서 숨김.
              live_service / cta_channel_id 값은 유지되며 UI 만 노출하지 않는다.

            {ctaType === 'internal' && isLive ? (
              <>
                <div>
                  <label style={labelStyle}>Service</label>
                  <Select
                    style={{ width: 240 }}
                    value={(value.live_service as LiveService) || undefined}
                    placeholder="서비스 선택"
                    onChange={v =>
                      set({ live_service: v ?? null, cta_channel_id: null })
                    }
                    options={LIVE_SERVICE_OPTIONS}
                  />
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    한 mediahub shorts row 는 한 서비스의 한 채널과 1:1 매핑.
                    양쪽 서비스에 보내려면 row 를 2개 만드세요.
                  </div>
                </div>
                {value.live_service === 'odk' || value.live_service === 'amasian' ? (
                  <div>
                    <label style={labelStyle}>Channel (live deeplink)</label>
                    <LiveChannelPicker
                      value={value.cta_channel_id ?? null}
                      onChange={v => set({ cta_channel_id: v })}
                      service={value.live_service as LiveService}
                    />
                  </div>
                ) : null}
              </>
            ) : null}
            */}

            {ctaType === 'external' ? (
              <div>
                <label style={labelStyle}>CTA URL (outlink, UTM 권장)</label>
                <Input
                  placeholder="https://..."
                  value={value.cta_url ?? ''}
                  onChange={e => set({ cta_url: e.target.value || null })}
                />
              </div>
            ) : null}

            <div>
              <label style={labelStyle}>Impression tracking URL (optional)</label>
              <Input
                placeholder="https://tracker.example/imp?..."
                value={value.impression_tracking_url ?? ''}
                onChange={e =>
                  set({ impression_tracking_url: e.target.value || null })
                }
              />
            </div>
          </div>
        </Card>
      </div>
    </Card>
  );
};

export default ShortsMetaSection;
